import {
  createConversationAPI,
  sendMessageAPI,
  getMessagesAPI,
  sendAttachmentAPI,
  toggleTyping,
  setUserLastSeenAt,
  toggleStatus,
  setCustomAttributes,
  deleteCustomAttribute,
  getConversationListAPI,
} from 'widget/api/conversation';
import { LocalStorage } from '../../../../shared/helpers/localStorage';

import { createTemporaryMessage, getNonDeletedMessages } from './helpers';
import { getLastConversationLSKey } from '../../../helpers/urlParamsHelper';

export const actions = {
  getConversationList: async ({ commit, dispatch, getters: appGetters }) => {
    const hasLoadedConversations = appGetters.getHasLoadedConversations;

    commit('setConversationUIFlag', {
      isFetchingConversationList: true,
      hasLoadedConversations: hasLoadedConversations || false,
    });
    try {
      const { data } = await getConversationListAPI();
      commit('setAllConversations', data);
      if (data.length === 1) {
        dispatch(
          'conversationAttributes/updateQueueStatus',
          {
            in_queue: data[0].in_queue,
          },
          { root: true }
        );
      }
    } catch (error) {
      // Ignore error
    }
    commit('setConversationUIFlag', {
      isFetchingConversationList: false,
      hasLoadedConversations: true,
    });
  },
  createConversation: async ({ commit, dispatch }, params) => {
    commit('setConversationUIFlag', { isCreating: true });
    try {
      const { data } = await createConversationAPI(params);
      const { messages } = data;
      const [message = {}] = messages;
      commit('pushMessageToConversation', message);
      await dispatch('contacts/get', null, { root: true });
      // dispatch('conversationAttributes/getAttributes', {}, { root: true });
    } catch (error) {
      // Ignore error
    } finally {
      commit('setConversationUIFlag', { isCreating: false });
    }
  },
  sendMessage: async ({ dispatch }, params) => {
    const { content, isNewConversation, replyTo } = params;
    const message = createTemporaryMessage({ content, replyTo });

    await dispatch('sendMessageWithData', message, isNewConversation);
  },
  updateMessage: async ({ commit }, message) => {
    commit('updateMessage', message);
  },
  sendMessageWithData: async (
    { commit, getters: appGetters },
    message,
    // eslint-disable-next-line no-unused-vars
    _isNewConversation
  ) => {
    const { id, content, replyTo, meta = {} } = message;
    const conversationSize = appGetters.getConversationSize;

    commit('pushMessageToConversation', message);
    commit('updateMessageMeta', { id, meta: { ...meta, error: '' } });
    try {
      const { data } = await sendMessageAPI(
        content,
        !conversationSize,
        replyTo
      );

      commit('deleteMessage', message.id);
      commit('pushMessageToConversation', { ...data, status: 'sent' });
    } catch (error) {
      commit('pushMessageToConversation', { ...message, status: 'failed' });
      commit('updateMessageMeta', {
        id,
        meta: { ...meta, error: '' },
      });
    }
  },

  setLastMessageId: async ({ commit }) => {
    commit('setLastMessageId');
  },
  setLastMessageOnConversation: async ({ commit }, data) => {
    commit('setLastMessageOnConversation', data);
  },
  setMessageStatusOnConversation: async ({ commit }, data) => {
    commit('setMessageStatusOnConversation', data);
  },
  sendAttachment: async ({ commit }, params) => {
    const { attachment, meta = {}, conversationId } = params;
    const attachments = Array.isArray(attachment)
      ? attachment.map(({ thumbUrl, fileType, id }) => ({
          thumb_url: thumbUrl,
          data_url: thumbUrl,
          file_type: fileType,
          status: 'in_progress',
          id,
        }))
      : [
          {
            thumb_url: attachment.thumbUrl,
            data_url: attachment.thumbUrl,
            file_type: attachment.fileType,
            status: 'in_progress',
          },
        ];

    const tempMessage = createTemporaryMessage({
      attachments,
      conversationId,
      content: params.content,
      replyTo: params.replyTo,
    });
    commit('pushMessageToConversation', tempMessage);
    try {
      const { data } = await sendAttachmentAPI(params);
      commit('updateAttachmentMessageStatus', {
        message: data,
        tempId: tempMessage.id,
      });
      commit('pushMessageToConversation', { ...data, status: 'sent' });
      return data;
    } catch (error) {
      commit('pushMessageToConversation', { ...tempMessage, status: 'failed' });
      commit('updateMessageMeta', {
        id: tempMessage.id,
        meta: { ...meta, error: '' },
      });
      return 'not_delivered';
    }
  },
  fetchOldConversations: async ({ commit }, { before } = {}) => {
    try {
      commit('setConversationListLoading', true);
      const params = { before };
      if (LocalStorage.get(getLastConversationLSKey())) {
        params.conversation_id = LocalStorage.get(
          getLastConversationLSKey()
        ).id;
      }
      const {
        data: { payload, meta },
      } = await getMessagesAPI(params);
      const { contact_last_seen_at: lastSeen } = meta;
      const formattedMessages = getNonDeletedMessages({ messages: payload });
      commit('conversation/setMetaUserLastSeenAt', lastSeen, { root: true });
      commit('setMessagesInConversation', formattedMessages);
      commit('setConversationListLoading', false);
    } catch (error) {
      commit('setConversationListLoading', false);
    }
  },
  fetchCurrentConversationMessages: async ({ commit }, { conversation_id }) => {
    commit('setIsFetchingMessages', true);
    try {
      const {
        data: { payload, meta },
      } = await getMessagesAPI({
        conversation_id,
      });
      const { contact_last_seen_at: lastSeen } = meta;
      const formattedMessages = getNonDeletedMessages({ messages: payload });
      commit('conversation/setMetaUserLastSeenAt', lastSeen, { root: true });
      commit('setMessagesInConversation', formattedMessages);
      commit('setIsFetchingMessages', false);
    } catch (error) {
      commit('setIsFetchingMessages', false);
    }
  },

  syncLatestMessages: async ({ state, commit }) => {
    try {
      const { lastMessageId, conversations } = state;
      const params = { after: lastMessageId };

      const data =
        Object.values(conversations)?.find(
          item =>
            item.id &&
            item.conversation &&
            item.conversation.id &&
            !item?.content_attributes?.is_greeting_message
        ) || {};

      params.conversation_id =
        data.conversation?.id || window.hooryCurrentConversation || '';

      const {
        data: { payload, meta },
      } = await getMessagesAPI(params);

      const { contact_last_seen_at: lastSeen } = meta;

      const formattedMessages = getNonDeletedMessages({ messages: payload });

      const missingMessages = formattedMessages.filter(
        message =>
          conversations?.[message.id] === undefined &&
          message.conversation_id === data.conversation_id &&
          !message.content_attributes?.is_greeting_message
      );

      if (!missingMessages.length) return;

      missingMessages.forEach(message => {
        conversations[message.id] = message;
      });

      // Sort conversation messages by created_at
      const updatedConversation = Object.fromEntries(
        Object.entries(conversations).sort(
          (a, b) => a[1].created_at - b[1].created_at
        )
      );

      commit('conversation/setMetaUserLastSeenAt', lastSeen, { root: true });
      commit('setMissingMessagesInConversation', updatedConversation);
      commit('setLastMessageId');
    } catch (error) {
      // IgnoreError
    }
  },

  clearConversations: ({ commit }) => {
    commit('clearConversations');
  },

  addOrUpdateMessage: async ({ commit }, data) => {
    const { id, content_attributes, conversation_id } = data;

    // eslint-disable-next-line no-unused-expressions
    window.logger &&
      window.logger.logUserAction(
        'create-message-info',
        'Message is added or updated vuex',
        {
          isAgentMessage: data.sender_type !== 'Contact',
          accountId: data?.account_id,
          messageId: data?.id,
          content: data?.content,
          createdAt: data?.id,
          messageType: data?.message_type,
          status: data?.status,
          senderId: data?.sender?.id,
          senderName: data?.sender?.name,
          conversationId: data?.conversation_id,
          inboxId: data?.inbox_id,
          activeConversationVuexId: window?.hooryCurrentConversationDid || '',
          activeConversationLocalStorageId: LocalStorage.get(
            getLastConversationLSKey()
          ),
          isWidgetOpen: data.isWidgetOpen,
        }
      );

    commit('readConversationMessages', { conversationId: conversation_id });

    if (content_attributes && content_attributes.deleted) {
      commit('deleteMessage', id);
      return;
    }
    commit('pushMessageToConversation', data);
  },

  toggleAgentTyping({ commit }, data) {
    commit('toggleAgentTypingStatus', data);
  },
  readConversationMessages({ commit }, data) {
    commit('readConversationMessages', data);
  },

  toggleAgentTypingList({ commit }, data) {
    commit('toggleAgentTypingStatusList', data);
  },
  changeConversationStatusList({ commit }, data) {
    commit('changeConversationStatusList', data);
  },
  setConversationUIFlag({ commit }, data) {
    commit('setConversationUIFlag', data);
  },

  toggleUserTyping: async (_, data) => {
    try {
      await toggleTyping(data);
    } catch (error) {
      // IgnoreError
    }
  },

  setUserLastSeen: async ({ commit, getters: appGetters }) => {
    if (!appGetters.getConversationSize && !window.hooryCurrentConversation) {
      return;
    }

    const lastSeen = Date.now() / 1000;
    try {
      commit('setMetaUserLastSeenAt', lastSeen);
      await setUserLastSeenAt({ lastSeen });
    } catch (error) {
      // IgnoreError
    }
  },

  resolveConversation: async () => {
    await toggleStatus();
  },

  setCustomAttributes: async (_, customAttributes = {}) => {
    try {
      await setCustomAttributes(customAttributes);
    } catch (error) {
      // IgnoreError
    }
  },

  deleteCustomAttribute: async (_, customAttribute) => {
    try {
      await deleteCustomAttribute(customAttribute);
    } catch (error) {
      // IgnoreError
    }
  },
};
